import axios from 'axios'
import JSONBig from 'json-bigint'
import { getStorage, clearUserStorage, STORAGE_KEYS } from '@/utils/storage'

// 使用json-bigint处理响应数据，解决bigint精度丢失问题
const JSONBigNative = JSONBig({ useNativeBigInt: true })

// 深度遍历对象，将 BigInt 转换为字符串
const convertBigIntToString = (obj) => {
  if (obj === null || obj === undefined) {
    return obj
  }
  
  if (typeof obj === 'bigint') {
    return obj.toString()
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => convertBigIntToString(item))
  }
  
  if (typeof obj === 'object') {
    const newObj = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = convertBigIntToString(obj[key])
      }
    }
    return newObj
  }
  
  return obj
}

// 根据环境配置 baseURL
// 从环境变量文件中读取配置
const getBaseURL = () => {
  // 优先使用环境变量文件中的配置
  if (process.env.VUE_APP_API_BASE_URL) {
    return process.env.VUE_APP_API_BASE_URL
  }
  // 如果没有配置环境变量，使用默认值（向后兼容）
  return process.env.NODE_ENV === 'production' 
    ? '/'
    : 'http://localhost:8840'
}

// 创建axios实例
const request = axios.create({
  baseURL: getBaseURL(),
  timeout: 0, // 不限制超时时间（0表示无超时限制）
  headers: {
    'Content-Type': 'application/json'
  },
  // 使用transformResponse在axios解析响应之前使用json-bigint处理
  transformResponse: [
    function(data) {
      // 如果data是字符串，使用json-bigint解析
      if (typeof data === 'string') {
        try {
          const parsed = JSONBigNative.parse(data)
          // 将BigInt转换为字符串，避免Vue响应式系统报错
          return convertBigIntToString(parsed)
        } catch (e) {
          // 如果解析失败，返回原始数据
          return data
        }
      }
      return data
    }
  ]
})

// 请求拦截器：自动添加token到请求头
request.interceptors.request.use(
  config => {
    // 从storage获取token
    const token = getStorage(STORAGE_KEYS.TOKEN)
    if (token) {
      // 添加Authorization头，格式：Bearer {token}
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 如果是 FormData，不设置 Content-Type，让浏览器自动设置（包含 boundary）
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器：处理响应和错误
request.interceptors.response.use(
  response => {
    // transformResponse已经处理了json-bigint解析，这里直接返回
    return response
  },
  error => {
    // 处理错误响应
    if (error.response) {
      // 401未授权，清除token并跳转到登录页
      if (error.response.status === 401) {
        // 清除用户相关存储
        clearUserStorage()
        
        // 如果当前不在登录页，则跳转到登录页
        const currentPath = window.location.pathname
        if (currentPath !== '/login') {
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

export default request